// hooks/useConfiguration.ts

import { useCallback } from 'react';
import { useMessage } from '@/shared/hooks/useMessage';
import {
    useGetAllActionFlowConfigurations,
    useGetActionFlowConfiguration,
    useCreateActionFlowConfiguration,
    useUpdateActionFlowConfiguration,
    useDeleteActionFlowConfiguration,
} from '../../api';
import { useConfigurationStore } from '../stores/configurationStore';
import { ActionFlowConfigurationDTO, ConfigurationFilter } from '../../api';
import {WorkflowConfig} from "@/modules/action-flow/config";


export const useActionFlowConfigurations = (filter: ConfigurationFilter = {}) => {
    const store = useConfigurationStore();
    const message = useMessage();

    const { data, isLoading, error,refetch } = useGetAllActionFlowConfigurations(
        { filter },
        {
            query: {
                // @ts-ignore
                onError: (error) => {
                    store.setError(error);
                    message.error('Failed to fetch configurations');
                }
            }
        }
    );

    return {
        configurations: data ?? store.configurations,
        isLoading,
        refetch,
        error
    };
};

export const useActionFlowConfiguration = (id: string) => {
    const store = useConfigurationStore();
    const message = useMessage();

    const { data, isLoading } = useGetActionFlowConfiguration(
        id,
        {
            query: {
                enabled: !!id,
                // @ts-ignore
                onError: (error) => {
                    store.setError(error);
                    message.error('Failed to fetch configuration');
                }
            }
        }
    );

    return {
        configuration: data ?? store.activeConfiguration,
        isLoading
    };
};

export const useManageConfiguration = () => {
    const store = useConfigurationStore();
    const message = useMessage();

    const createMutation = useCreateActionFlowConfiguration();
    const updateMutation = useUpdateActionFlowConfiguration();
    const deleteMutation = useDeleteActionFlowConfiguration();

    const createConfiguration = useCallback(async (data: ActionFlowConfigurationDTO) => {
        try {
            const response = await createMutation.mutateAsync({ data });
            store.addConfiguration(response);
            message.success('Configuration created successfully');
            return response;
        } catch (error) {
            message.error('Failed to create configuration');
            throw error;
        }
    }, [createMutation, store, message]);

    const updateConfiguration = useCallback(async (id: string, data: ActionFlowConfigurationDTO) => {
        try {
            const updatedConfig = await updateMutation.mutateAsync({ id, data });

            // Check if a new version was created (different ID means versioning occurred)
            if (updatedConfig.id !== id) {
                store.addConfiguration(updatedConfig);
                // @ts-ignore
                message.success({
                    content: `Configuration has active workflows. Created new version ${updatedConfig.version} with updated changes. ` +
                    `Existing workflows will continue on the previous version.`,
                    duration: 8
                });
            } else {
                store.updateConfiguration(updatedConfig);
                message.success('Configuration updated successfully');
            }

            return updatedConfig;
        } catch (error) {
            message.error('Failed to update configuration');
            throw error;
        }
    }, [updateMutation, store, message]);

    const deleteConfiguration = useCallback(async (id: string) => {
        try {
            await deleteMutation.mutateAsync({ id });
            store.removeConfiguration(id);
            message.success('Configuration deleted successfully');
        } catch (error) {
            message.error('Failed to delete configuration');
            throw error;
        }
    }, [deleteMutation, store, message]);

    const validateConfiguration = useCallback((config: WorkflowConfig): boolean => {
        // Validate that there's exactly one maker node
        const makerNodes = config.states.filter(node => node.type === 'maker');
        if (makerNodes.length !== 1) {
            message.error('Configuration must have exactly one maker node');
            return false;
        }

        // Validate that there's at least one end state
        const endStates = config.states.filter(node =>
            node.type === 'approved' || node.type === 'rejected'
        );
        if (endStates.length === 0) {
            message.error('Configuration must have at least one end state');
            return false;
        }

        // Validate node levels
        const invalidLevels = config.states.some(node => {
            if (node.type === 'maker' && node.data.level !== 0) return true;
            if ((node.type === 'approved' || node.type === 'rejected') && node.data.level !== 100) return true;
            return false;
        });
        if (invalidLevels) {
            message.error('Invalid node levels detected');
            return false;
        }

        return true;
    }, [message]);

    return {
        createConfiguration,
        updateConfiguration,
        deleteConfiguration,
        validateConfiguration,
        isLoading: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending
    };
};